import { hatBackgroundSync, nachreichen, wartende } from './warteschlange.js'

/**
 * Der Nachreicher — trägt wartende Alarme aus, sobald wieder Netz da ist.
 *
 * ## Der Befund (CEO-Bewertung 2026-09-12)
 *
 * `warteschlange.ts` versprach seit Phase 11: „Wo Background Sync fehlt,
 * greift der Fallback in `App.tsx`: erneut versuchen, sobald der Browser
 * wieder online meldet." Die Funktion dafür, `nachreichen()`, war fertig
 * geschrieben, getestet gegen nichts — und hatte **keinen einzigen Aufrufer**.
 * Safari kennt kein Background Sync. Auf dem iPhone, dem Gerät, das die
 * Familie tatsächlich benutzt, blieb ein im Funkloch ausgelöster Alarm also
 * für immer in IndexedDB liegen, und der Bildschirm sagte „wird gesendet,
 * sobald Verbindung da ist". Dieselbe Fehlerklasse wie dreimal zuvor:
 * gebaut, dokumentiert, nie angeschlossen.
 *
 * ## Was er tut
 *
 * Er versucht es zu vier Gelegenheiten, und nur wenn etwas wartet:
 *
 *   1. beim Start der App — der Alarm kann aus einer früheren Sitzung stammen,
 *   2. wenn der Browser `online` meldet,
 *   3. wenn die Seite wieder sichtbar wird — auf dem Telefon kommt das Netz
 *      oft zurück, während die App im Hintergrund lag, und `online` verpasst,
 *   4. alle 30 Sekunden — `navigator.onLine` sagt nur, dass ein Netz da ist,
 *      nicht, dass der Server antwortet.
 *
 * Er läuft AUCH dort, wo Background Sync existiert. Der Service Worker und
 * die Seite können denselben Alarm dann zweimal schicken; der Server führt
 * Wiederholungen zusammen (ROADMAP Phase 11), ein doppelter Versuch kostet
 * nichts, ein verpasster kostet alles.
 *
 * ## Was er nicht kann
 *
 * Er lebt in der Seite. Ist die App zu, tut er nichts — das kann nur
 * Background Sync, und das gibt es auf iOS nicht. Deshalb sagt `Sos.tsx` dem
 * Nutzer dort ausdrücklich, dass er die App offen lassen muss.
 */

export interface NachreichErgebnis {
  /** So viele Alarme hat der Server gerade angenommen. */
  nachgereicht: number
  /** So viele warten weiter. */
  offen: number
}

export const NACHREICH_INTERVALL_MS = 30_000

/**
 * Startet den Nachreicher. Gibt die Funktion zurück, die ihn wieder anhält.
 *
 * `onErgebnis` wird nur gerufen, wenn wirklich etwas nachgereicht wurde oder
 * weiter wartet — nicht bei jedem leeren Durchlauf.
 */
export function nachreicherStarten(onErgebnis?: (e: NachreichErgebnis) => void): () => void {
  let laeuft = false
  let angehalten = false

  async function versuch(): Promise<void> {
    if (laeuft || angehalten) return
    laeuft = true
    try {
      const offen = await wartende().catch(() => [])
      if (offen.length === 0) return
      if (!navigator.onLine) {
        onErgebnis?.({ nachgereicht: 0, offen: offen.length })
        return
      }
      const nachgereicht = await nachreichen()
      const rest = await wartende().catch(() => [])
      onErgebnis?.({ nachgereicht, offen: rest.length })
    } finally {
      laeuft = false
    }
  }

  const beiOnline = () => { void versuch() }
  const beiSichtbar = () => { if (!document.hidden) void versuch() }
  addEventListener('online', beiOnline)
  document.addEventListener('visibilitychange', beiSichtbar)
  const takt = setInterval(() => { void versuch() }, NACHREICH_INTERVALL_MS)
  void versuch()

  return () => {
    angehalten = true
    removeEventListener('online', beiOnline)
    document.removeEventListener('visibilitychange', beiSichtbar)
    clearInterval(takt)
  }
}

/** Muss die App offen bleiben, damit ein wartender Alarm rausgeht? */
export function brauchtOffeneApp(): boolean {
  return !hatBackgroundSync()
}
